/** One node-value dependency: the from-node's value reads the to-node's value. */
export interface ValueReferenceEdge {
  fromNodeId: number;
  toNodeId: number;
  /**
   * How the from-node depends on the to-node: an authored reference (default), or the
   * to-node being a child whose result is folded into the from-node's own.
   */
  kind?: "reference" | "child-result";
  /** Where the reference is authored on the from-node (binding or value name), or the child slot. */
  name?: string;
  /** The referenced property/value on the to-node, when known. */
  property?: string;
}

/**
 * A cyclic dependency between node values. `edges` walks the loop in reference
 * order; the last edge's `toNodeId` equals the first edge's `fromNodeId`.
 */
export interface ValueReferenceCycle {
  nodeIds: number[];
  edges: ValueReferenceEdge[];
}

/**
 * Detect reference cycles in the node-value dependency graph (a node's value bound
 * to another node's, directly or transitively, including self-references).
 *
 * Every node participating in any cycle is reported. Nodes that are mutually
 * reachable (one strongly connected component) are reported as one cycle with a
 * shortest representative chain, so a component tangled by several overlapping
 * loops yields a single actionable warning rather than a combinatorial listing.
 * Acyclic sharing (diamonds) is never reported.
 */
export const findValueReferenceCycles = (edges: readonly ValueReferenceEdge[]): ValueReferenceCycle[] => {
  const successors = new Map<number, ValueReferenceEdge[]>();
  for (const edge of edges) {
    const list = successors.get(edge.fromNodeId);
    if (list) {
      list.push(edge);
    } else {
      successors.set(edge.fromNodeId, [edge]);
    }
    if (!successors.has(edge.toNodeId)) successors.set(edge.toNodeId, []);
  }

  const components = stronglyConnectedComponents(successors);

  const cycles: ValueReferenceCycle[] = [];
  for (const component of components) {
    if (component.length > 1) {
      cycles.push(representativeCycle(component, successors));
      continue;
    }
    const [nodeId] = component;
    const selfEdge = successors.get(nodeId)?.find((edge) => edge.toNodeId === nodeId);
    if (selfEdge) cycles.push({ nodeIds: [nodeId], edges: [selfEdge] });
  }
  return cycles;
};

/** Tarjan's algorithm (iterative); returns only the components, unordered. */
const stronglyConnectedComponents = (successors: ReadonlyMap<number, ValueReferenceEdge[]>): number[][] => {
  const indexOf = new Map<number, number>();
  const lowLink = new Map<number, number>();
  const onStack = new Set<number>();
  const stack: number[] = [];
  const components: number[][] = [];
  let nextIndex = 0;

  const visit = (root: number) => {
    // frames: [nodeId, next successor position to explore]
    const frames: Array<[number, number]> = [[root, 0]];
    while (frames.length > 0) {
      const frame = frames[frames.length - 1];
      const [nodeId, position] = frame;
      if (position === 0) {
        indexOf.set(nodeId, nextIndex);
        lowLink.set(nodeId, nextIndex);
        nextIndex++;
        stack.push(nodeId);
        onStack.add(nodeId);
      }
      const outgoing = successors.get(nodeId) ?? [];
      if (position < outgoing.length) {
        frame[1]++;
        const successor = outgoing[position].toNodeId;
        if (!indexOf.has(successor)) {
          frames.push([successor, 0]);
        } else if (onStack.has(successor)) {
          lowLink.set(nodeId, Math.min(lowLink.get(nodeId)!, indexOf.get(successor)!));
        }
        continue;
      }
      frames.pop();
      const parent = frames[frames.length - 1];
      if (parent) {
        lowLink.set(parent[0], Math.min(lowLink.get(parent[0])!, lowLink.get(nodeId)!));
      }
      if (lowLink.get(nodeId) === indexOf.get(nodeId)) {
        const component: number[] = [];
        let member: number;
        do {
          member = stack.pop()!;
          onStack.delete(member);
          component.push(member);
        } while (member !== nodeId);
        components.push(component);
      }
    }
  };

  for (const nodeId of successors.keys()) {
    if (!indexOf.has(nodeId)) visit(nodeId);
  }
  return components;
};

/**
 * Shortest loop through the component's smallest node id (canonical start, so
 * repeated detection reports the same chain): BFS within the component from the
 * start's successors back to the start.
 */
const representativeCycle = (
  component: number[],
  successors: ReadonlyMap<number, ValueReferenceEdge[]>,
): ValueReferenceCycle => {
  const members = new Set(component);
  const start = Math.min(...component);

  const arrivedBy = new Map<number, ValueReferenceEdge>();
  const queue: number[] = [start];
  for (let head = 0; head < queue.length; head++) {
    for (const edge of successors.get(queue[head]) ?? []) {
      if (!members.has(edge.toNodeId)) continue;
      if (edge.toNodeId === start) {
        const loop = [edge];
        let cursor = edge.fromNodeId;
        while (cursor !== start) {
          const inbound = arrivedBy.get(cursor)!;
          loop.unshift(inbound);
          cursor = inbound.fromNodeId;
        }
        return { nodeIds: loop.map((hop) => hop.fromNodeId), edges: loop };
      }
      if (!arrivedBy.has(edge.toNodeId)) {
        arrivedBy.set(edge.toNodeId, edge);
        queue.push(edge.toNodeId);
      }
    }
  }
  // Unreachable: a strongly connected component always loops back to start.
  throw new Error(`No cycle found through node ${start} in component [${component.join(", ")}]`);
};
